import React from 'react';
import { TextInput, View } from 'react-native';
import type { KeyboardTypeOptions } from 'react-native';
import { fontStyle, radius, scaleFont, useColors, useTextScale } from '../theme';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { T } from './Text';

export function TextField({
  label,
  icon,
  value,
  onChangeText,
  placeholder,
  keyboardType,
  secureTextEntry,
  onSubmitEditing,
  testID,
  editable = true,
  minWidth = 160,
}: {
  label: string;
  icon?: IconName;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  keyboardType?: KeyboardTypeOptions;
  secureTextEntry?: boolean;
  onSubmitEditing?: () => void;
  testID?: string;
  /** Read-only fields are dimmed. Used for a primary key that cannot change. */
  editable?: boolean;
  /**
   * Narrowest this field may get before a wrapping row breaks it onto its own
   * line. The 160 default is what makes a pair of fields stack on a phone.
   *
   * Lower it for a field that is genuinely short — a price, a quantity — so a
   * pair still fits one row on a narrow screen. React Native defaults
   * flexShrink to 0, so this floor is hard: two 160s plus a gap need 328px and
   * simply overflow a 307px sheet rather than shrinking to fit.
   */
  minWidth?: number;
}) {
  const colors = useColors();
  const scale = useTextScale();
  return (
    <View style={{ gap: 6, flexGrow: 1, flexShrink: 1, flexBasis: 'auto', minWidth }}>
      <T size={11.5} weight={600} color={colors.textFaint}>
        {label}
      </T>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 9,
          backgroundColor: colors.surface2,
          borderWidth: 1,
          borderColor: colors.line,
          borderRadius: radius.md,
          paddingHorizontal: 12,
          opacity: editable ? 1 : 0.6,
        }}
      >
        {icon ? <Icon name={icon} size={15} color={colors.textFaint} /> : null}
        <TextInput
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.textFaint}
          keyboardType={keyboardType}
          secureTextEntry={secureTextEntry}
          onSubmitEditing={onSubmitEditing}
          editable={editable}
          autoCapitalize="none"
          autoCorrect={false}
          testID={testID}
          style={[
            {
              flex: 1,
              paddingVertical: 11,
              color: colors.text,
              fontSize: scaleFont(14, scale),
              outlineStyle: 'none',
            } as any,
            fontStyle(600),
          ]}
        />
      </View>
    </View>
  );
}
